import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import AgentLayout, { agentFetch } from './AgentLayout';
import ResumePhase from './ResumePhase';
import { useKeyboardShortcuts, ShortcutsHelpModal, ShortcutsHint } from '../../utils/useKeyboardShortcuts';
import { useAgentDirectory, ReferentTag } from '../../utils/useAgentDirectory';
import { useResumableChat } from '../../utils/useResumableChat';
import { buildCloseCallNote } from '../../utils/callNote';

// ── Liste des raccourcis affichés dans la modal d'aide ────────────────────────

const SESSION_SHORTCUTS = [
  { combo: 'Échap',          label: 'Retour aux clients (ou fermer la modale ouverte)' },
  { combo: 'Ctrl/Cmd + Entrée', label: "Ouvrir « Terminer l'appel »" },
  { combo: 'Ctrl/Cmd + 1',    label: 'Onglet Aides (sur l\'écran résultats)' },
  { combo: 'Ctrl/Cmd + 2',    label: 'Onglet Email (sur l\'écran résultats)' },
  { combo: 'Ctrl/Cmd + S',    label: 'Sauvegarder le dossier (si modifié)' },
  { combo: '?',               label: 'Afficher cette aide' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

// Phase 6 / G2 — LeadDto a un seul champ `nom` (string), pas de prénom/nom
// séparés. On garde l'API `fullName(c)` pour préserver les call-sites mais
// on lit `c.nom` désormais.
function fullName(c) { return c?.nom || 'Client'; }

/**
 * Construit un résumé textuel du client à injecter dans le system prompt
 * du chat IA. Le backend attend une STRING (pas un objet) — ce helper
 * garantit que l'on envoie toujours un résumé lisible ou undefined.
 *
 * Fix bug : avant, on envoyait { nom, profil } (objet), ce qui causait
 * un TypeError dans AnthropicChatGateway.buildSystemPrompt → fallback.
 */
function clientContextStr(client) {
  if (!client) return undefined;
  const p = client.profil || {};
  const parts = [
    client.nom && `Nom : ${client.nom}`,
    p.age && `${p.age} ans`,
    p.departement && `Département ${p.departement}`,
    p.situationLogement && `Logement : ${p.situationLogement}`,
    p.revenuFiscalReference && `Revenus : ${p.revenuFiscalReference} €/an`,
    p.girNiveau && `GIR ${p.girNiveau}`,
    p.handicap === true && 'Handicap reconnu',
    p.ald === true && 'ALD',
    Array.isArray(p.besoins) && p.besoins.length && `Besoins : ${p.besoins.join(', ')}`,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : undefined;
}

/**
 * Phase 6 / G2 — adaptateur LeadProfilDto → MatchAidesProfilSnapshotInput.
 * Le matching backend (POST /api/matching/run) attend un profil normalisé
 * (cf. MatchAidesProfilSnapshotInput) : on filtre les champs `undefined`
 * pour préserver exactOptionalPropertyTypes côté backend.
 *
 * Champs de mapping :
 *  - age, departement : obligatoires (le matching lève si absents)
 *  - revenuFiscalReference, girNiveau, situationLogement, situationFamiliale,
 *    handicap, ald, besoins : optionnels
 */
function toMatchingSnapshot(profil) {
  if (!profil) return null;
  const snap = {
    age: profil.age,
    departement: profil.departement,
  };
  if (profil.revenuFiscalReference !== undefined) snap.revenuFiscalReference = profil.revenuFiscalReference;
  if (profil.girNiveau !== undefined)             snap.girNiveau = profil.girNiveau;
  if (profil.situationLogement !== undefined)     snap.situationLogement = profil.situationLogement;
  if (profil.situationFamiliale !== undefined)    snap.situationFamiliale = profil.situationFamiliale;
  if (profil.handicap !== undefined)              snap.handicap = profil.handicap;
  if (profil.ald !== undefined)                   snap.ald = profil.ald;
  if (Array.isArray(profil.besoins))              snap.besoins = profil.besoins;
  return snap;
}

// Phase 6 / G2 — la liste DEPTS (codes département) était utilisée par le
// formulaire d'édition inline du dossier client (supprimé : édition se fait
// désormais via PUT /api/agent/leads/:id/profil au sein du cycle interview).

function deriveIneligibilite(aide, client) {
  const points = [];
  const minAge = aide.conditions_age ?? 60;
  if (client?.age && client.age < minAge) points.push(`Âge insuffisant (min. ${minAge} ans)`);
  else points.push(`Moins de ${minAge} ans`);
  if (aide.nom?.toLowerCase().includes('apa')) points.push('GIR 5 ou 6 (autonomie préservée)');
  const r = aide.conditions_ressources || '';
  if (r && r.toLowerCase() !== 'pas de plafond' && r.toLowerCase().includes('plafond'))
    points.push(`Dépassement plafond de ressources`);
  if (aide.couverture_geographique !== 'nationale')
    points.push('Résidence hors zone de couverture');
  if (aide.nom?.toLowerCase().includes('domicile') && client?.situation_logement === 'ehpad')
    points.push('Aide pour domicile — client en EHPAD');
  return points.slice(0, 4);
}

// ── Composants UI partagés ────────────────────────────────────────────────────

function Badge({ color, children }) {
  return <span style={{ display:'inline-block', padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:600, background:color+'20', color }}>{children}</span>;
}

// ── Dossier sidebar (partagée interview + résultats) ─────────────────────────

function DossierSidebar({ client, liveProfil, step, onRestart }) {
  // Phase 6 / G2 — LeadDto/GetLeadFullOutput :
  //   client = { id, nom, email, telephone, source, statut, assignedAgentId,
  //              notes, profil: LeadProfilDto | null, dossierIds,
  //              lastInteractionAt, createdAt, updatedAt, dossiers? }
  // LeadProfilDto = { age, departement, revenuFiscalReference?, girNiveau?,
  //                   situationLogement?, handicap?, ald?, besoins? }
  // Plus de prénom/nom séparés ni aidant_info ni ville (le pivot interne
  // ne distingue plus senior/aidant côté lead).
  const p = { ...(client?.profil || {}), ...(liveProfil || {}) };

  const age      = p.age;
  const dept     = p.departement;
  const revenus  = p.revenuFiscalReference ? `${p.revenuFiscalReference.toLocaleString('fr-FR')} €/an (RFR)` : null;

  const rows = [
    ['Téléphone',  client?.telephone],
    ['Email',      client?.email],
    ['Âge',        age ? `${age} ans` : null],
    ['Département',dept],
    ['Logement',   p.situationLogement],
    ['Revenus',    revenus],
    ['GIR',        p.girNiveau ? `GIR ${p.girNiveau}` : null],
    ['Handicap',   p.handicap === true ? 'Oui' : null],
    ['ALD',        p.ald      === true ? 'Oui' : null],
    ['Statut',     client?.statut],
  ].filter(([, v]) => v);

  const besoins = Array.isArray(p.besoins) ? p.besoins : [];
  const progressKeys = ['age', 'departement', 'situationLogement', 'revenuFiscalReference', 'girNiveau'];
  const filled = progressKeys.filter(k => p[k] !== undefined && p[k] !== null).length;
  const name = client?.nom || 'Client';

  return (
    <div style={{ width:240, flexShrink:0, borderRight:'1px solid #E0DDD8', display:'flex', flexDirection:'column', overflow:'hidden', background:'#FDFCFA' }}>

      {/* En-tête client */}
      <div style={{ padding:'16px', borderBottom:'1px solid #E0DDD8' }}>
        <div style={{ fontWeight:700, fontSize:15, color:'#D4724A', marginBottom:6 }}>{name}</div>
        {client?.source && (
          <span style={{
            fontSize:11, fontWeight:600, padding:'2px 9px', borderRadius:20,
            background: '#F0FBF4', color: '#3D8B5E',
          }}>
            {client.source}
          </span>
        )}
      </div>

      {/* Progression (phase interview seulement) */}
      {step != null && (
        <div style={{ padding:'10px 16px', borderBottom:'1px solid #E0DDD8' }}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#8A8A8A', marginBottom:4 }}>
            <span>Profil collecté</span>
            <span>Q {step} / ~8</span>
          </div>
          <div style={{ height:4, background:'#E0DDD8', borderRadius:4 }}>
            <div style={{ height:4, background:'#3D8B5E', borderRadius:4, width:`${Math.min((filled/progressKeys.length)*100,100)}%`, transition:'width 0.4s' }} />
          </div>
        </div>
      )}

      {/* Champs */}
      <div style={{ flex:1, overflowY:'auto', padding:14 }}>
        {rows.length === 0 && step != null && (
          <div style={{ color:'#C0C0C0', fontSize:12, textAlign:'center', paddingTop:20, lineHeight:1.7 }}>
            Les informations s'affichent au fur et à mesure de l'entretien…
          </div>
        )}
        {rows.map(([label, value]) => (
          <div key={label} style={{ marginBottom:8, paddingBottom:7, borderBottom:'1px solid #F0EDE8' }}>
            <div style={{ fontSize:10, color:'#8A8A8A', textTransform:'uppercase', letterSpacing:0.4, marginBottom:1 }}>{label}</div>
            <div style={{ fontSize:13, color:'#2D2D2D', fontWeight:500 }}>{value}</div>
          </div>
        ))}
        {besoins.length > 0 && (
          <div style={{ marginTop:4 }}>
            <div style={{ fontSize:10, color:'#8A8A8A', textTransform:'uppercase', letterSpacing:0.4, marginBottom:5 }}>Besoins</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
              {besoins.map((b, i) => (
                <span key={i} style={{ fontSize:11, padding:'2px 8px', borderRadius:20, background:'#FFF0EA', color:'#D4724A' }}>{b}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {onRestart && (
        <div style={{ padding:'12px 14px', borderTop:'1px solid #E0DDD8' }}>
          <button onClick={onRestart} style={{
            width:'100%', padding:'7px', borderRadius:8, border:'1px solid #E0DDD8',
            background:'transparent', fontSize:12, cursor:'pointer', color:'#8A8A8A',
          }}>
            ↺ Recommencer l'entretien
          </button>
        </div>
      )}
    </div>
  );
}

// ── PHASE 1 : Entretien guidé ─────────────────────────────────────────────────

// Phase 6 / G2 — refactor en 2 calls :
//   1. POST /api/agent/interviews { leadId, clientName, withGreeting: true }
//      → 201 InterviewDto (turns initiaux + interview.id)
//   2. POST /api/agent/interviews/:id/messages { agentMessage, clientName }
//      → 200 { interview: InterviewDto, assistantReply: string }
//      à chaque réponse de l'utilisateur. L'interview accumule les turns
//      côté backend (état persisté Mongo). Quand `interview.pretPourMatching
//      === true`, on déclenche `onComplete(profilPartiel, ...)`.
function InterviewPhase({ client, clientId, resumeInterviewId, onComplete, onError }) {
  const [messages, setMessages]   = useState([]); // [{role, content}]
  const [input, setInput]         = useState('');
  const [profil, setProfil]       = useState({});
  const [step, setStep]           = useState(0);
  const [loading, setLoading]     = useState(true);
  const [sending, setSending]     = useState(false);
  const [interviewId, setInterviewId] = useState(null);
  const chatBottomRef             = useRef(null);
  const inputRef                  = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        let interview;
        if (resumeInterviewId) {
          const res = await agentFetch(`/api/agent/interviews/${resumeInterviewId}`);
          if (!res.ok) throw new Error('get interview failed');
          interview = await res.json();
        } else {
          const res = await agentFetch('/api/agent/interviews', {
            method: 'POST',
            body: JSON.stringify({
              leadId: clientId,
              clientName: fullName(client),
              withGreeting: true,
            }),
          });
          if (!res.ok) throw new Error('start interview failed');
          interview = await res.json();
        }
        applyInterview(interview);
      } catch {
        onError?.("La connexion à Claude a échoué. Vérifiez votre réseau puis réessayez.");
      } finally {
        setLoading(false);
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    })();
  }, [clientId, resumeInterviewId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const focusInput = () => { inputRef.current?.focus(); };

  // Applique un InterviewDto reçu du backend : sync les turns, profil
  // partiel, step (= nombre de turns user) et déclenche onComplete si
  // pretPourMatching.
  const applyInterview = (interview) => {
    if (!interview) return;
    setInterviewId(interview.id);
    const turns = Array.isArray(interview.turns) ? interview.turns : [];
    setMessages(turns.map(t => ({ role: t.role, content: t.content })));
    if (interview.profilPartiel) {
      setProfil(p => ({ ...p, ...interview.profilPartiel }));
    }
    setStep(turns.filter(t => t.role === 'user').length);
    if (interview.pretPourMatching) {
      // L'IA estime que le profil est suffisant pour le matching. On
      // remonte au parent qui appellera CompleteInterview (qui pousse
      // le profil sur le lead via UpdateLeadProfil) puis MatchAides.
      onComplete?.(interview.profilPartiel || {}, interview.id);
    }
  };

  const send = async () => {
    if (!input.trim() || sending || loading || !interviewId) return;
    const answer = input.trim();
    setInput('');
    setSending(true);
    // Affichage optimiste du message user
    setMessages(prev => [...prev, { role: 'user', content: answer }]);
    try {
      const res = await agentFetch(`/api/agent/interviews/${interviewId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ agentMessage: answer, clientName: fullName(client) }),
      });
      if (!res.ok) throw new Error('submit failed');
      const out = await res.json();
      applyInterview(out.interview);
    } catch {
      setMessages(h => [...h, { role: 'assistant', content: "Désolé, une erreur s'est produite. Veuillez réessayer." }]);
      onError?.("La connexion à Claude a échoué. Vérifiez votre réseau puis réessayez.");
    } finally {
      setSending(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  const height = 'calc(100vh - 80px)';

  return (
    <div style={{ display:'flex', gap:0, height, overflow:'hidden', borderRadius:14, border:'1px solid #E0DDD8', background:'#FDFCFA' }}>

      {/* Dossier à gauche */}
      <DossierSidebar
        client={client}
        liveProfil={profil}
        step={step}
      />

      {/* Zone chat principale */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>

        {/* Header */}
        <div style={{ padding:'12px 20px', borderBottom:'1px solid #E0DDD8', background:'#FDFCFA', fontSize:13, color:'#8A8A8A' }}>
          Entretien — lisez les questions au client, tapez sa réponse ci-dessous
        </div>

        {/* Bulles de conversation */}
        <div
          onClick={focusInput}
          style={{ flex:1, overflowY:'auto', padding:'20px', display:'flex', flexDirection:'column', gap:12, background:'#F5F3F0', cursor:'text' }}
        >
          {messages.map((m, i) => (
            <div key={i} style={{
              maxWidth:'80%',
              alignSelf: m.role === 'assistant' ? 'flex-start' : 'flex-end',
            }}>
              {m.role === 'assistant' && (
                <div style={{ fontSize:10, color:'#D4724A', fontWeight:700, marginBottom:3, textTransform:'uppercase', letterSpacing:0.5 }}>
                  Question à lire au client
                </div>
              )}
              <div style={{
                padding:'12px 16px',
                borderRadius: m.role === 'assistant' ? '4px 18px 18px 18px' : '18px 18px 4px 18px',
                fontSize:15, lineHeight:1.6,
                background: m.role === 'assistant' ? '#fff' : '#D4724A',
                color: m.role === 'assistant' ? '#2D2D2D' : '#fff',
                boxShadow:'0 1px 3px rgba(0,0,0,0.08)',
                fontWeight: m.role === 'assistant' ? 500 : 400,
              }}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ alignSelf:'flex-start', padding:'12px 16px', background:'#fff', borderRadius:'4px 18px 18px 18px', fontSize:15, color:'#8A8A8A', boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
              Claude prépare la question…
            </div>
          )}
          <div ref={chatBottomRef} />
        </div>

        {/* Zone de saisie */}
        <div style={{ padding:'12px 16px', background:'#FDFCFA', borderTop:'1px solid #E0DDD8', display:'flex', gap:10, alignItems:'flex-end' }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder="Tapez la réponse du client… (Entrée pour envoyer, Shift+Entrée pour une nouvelle ligne)"
            rows={2}
            disabled={loading}
            style={{
              flex:1, padding:'12px 16px', borderRadius:24, border:'1.5px solid #D9D9D9',
              fontSize:14, resize:'none', boxSizing:'border-box', outline:'none',
              fontFamily:'inherit', lineHeight:1.5,
              background: loading ? '#F5F3F0' : '#fff',
            }}
          />
          <button onClick={send} disabled={loading || sending || !input.trim()} style={{
            width:44, height:44, borderRadius:'50%', border:'none', flexShrink:0,
            background:(loading || sending || !input.trim()) ? '#D9D9D9' : '#D4724A',
            color:'#fff', fontSize:20, cursor:(loading || sending || !input.trim()) ? 'not-allowed' : 'pointer',
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>
            ↑
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PHASE 1b : Analyse en cours ───────────────────────────────────────────────

/**
 * Sprint Aides Discovery (étape 4) — UX du matching en 2 phases.
 *
 *   matchingPhase = 'initial'   → message bref (« Recherche des aides… »)
 *   matchingPhase = 'discovery' → bascule après ~3s : on suggère qu'aucun
 *     match BDD n'a été trouvé et que Claude consulte les sources officielles
 *     (web_search → service-public.fr, CAF, CNAV, département). Cette phase
 *     peut durer 30 à 60 secondes côté backend (DiscoverAidesFromProfile).
 *
 * On garde un overlay plein écran pour que l'agent comprenne que le wait
 * long est volontaire et pas un freeze réseau.
 */
function AnalysingPhase({ matchingPhase = 'initial' }) {
  const [dots, setDots] = useState('');
  useEffect(() => {
    const t = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 500);
    return () => clearInterval(t);
  }, []);

  const isDiscovery = matchingPhase === 'discovery';

  return (
    <div style={{
      position:'fixed', inset:0,
      background:'rgba(255, 248, 240, 0.95)',
      display:'flex', alignItems:'center', justifyContent:'center',
      zIndex:1000,
    }}>
      <div style={{
        background:'#fff', padding:32, borderRadius:14,
        maxWidth:520, width:'90%', textAlign:'center',
        boxShadow:'0 8px 32px rgba(0,0,0,0.15)',
        border:'1px solid #E0DDD8',
      }}>
        <div style={{ fontSize:48, marginBottom:12 }}>
          {isDiscovery ? '🔍' : '⏳'}
        </div>
        <h3 style={{ color:'#5A3C1F', marginTop:0, marginBottom:12, fontSize:18 }}>
          {isDiscovery
            ? 'Recherche dans les sources officielles'
            : `Analyse des aides en cours${dots}`}
        </h3>
        <p style={{ color:'#6B5847', fontSize:14, lineHeight:1.6, margin:0 }}>
          {isDiscovery
            ? "Aucune aide correspondante en base. Claude consulte service-public.fr, CAF, CNAV et le département. Cela peut prendre 30 à 60 secondes — merci de patienter."
            : "Claude analyse le profil du client et recherche les meilleures aides…"}
        </p>
        <div style={{
          marginTop:24, width:40, height:40,
          border:'4px solid #F5EFE6',
          borderTopColor:'#A0763E',
          borderRadius:'50%',
          margin:'24px auto 0',
          animation:'aidly-spin 1s linear infinite',
        }} />
        <style>{`@keyframes aidly-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

// ── PHASE 2 : Résultats ───────────────────────────────────────────────────────

// Formatage explicite d'une fourchette montant + unité. Spéciale "unique"
// pour éviter "€/unique" (illisible) → "€ (versement unique)" qui dit
// clairement au client que c'est une aide one-shot, pas récurrente.
function formatMontantRange(min, max, unite) {
  if (!max && !min) return '—';
  const range = max ? `${min ?? 0}–${max} €` : `jusqu'à ${min} €`;
  if (unite === 'unique') return `${range} (versement unique)`;
  if (!unite) return range; // Pas d'unité connue → on n'invente pas "/mois"
  return `${range}/${unite}`;
}

// P2 : questions universelles pré-suggérées dans le panneau IA. Liste
// volontairement courte (6 items) et orientée terrain call-center seniors
// — on évite les questions trop vagues qui produisent des réponses
// génériques. À enrichir au feedback agent (Phase 6+).
const SUGGESTED_QUESTIONS = [
  'Cette aide est-elle cumulable avec l\'APA ou l\'ASPA ?',
  'Combien de temps avant le déblocage des fonds ?',
  'Que faire si le dossier est refusé ?',
  'Y a-t-il un délai maximum pour faire la demande ?',
  'L\'aide est-elle imposable ou récupérable sur succession ?',
  'Quelle alternative si le client ne remplit pas toutes les conditions ?',
];

function AideCard({ aide, client, selected, onToggle }) {
  // Phase 6 / G2 — supporte les 2 shapes (legacy + RecommandationAideDto) :
  //   legacy : { id, montant_min, montant_max, unite, eligibilite_pct,
  //              priorite, motif, description, conditions_age, ... }
  //   nouveau: { aideId, nom, score, raisonTexte, raisonConfiance,
  //              montantEstimeMin, montantEstimeMax, documentsRequis,
  //              facilite }
  const [expanded, setExpanded] = useState(false);
  const inelig = deriveIneligibilite(aide, client);
  const score = aide.score ?? aide.eligibilite_pct;
  const hasPct = score != null;

  // Affichage fidèle : on préfère les montants ORIGINAUX (avant
  // annualisation) + l'unité d'origine. Sans ça, on affichait "0-22000 €/mois"
  // pour MaPrimeAdapt' (unique 22000 €) — fausse promesse au client.
  // Fallback : valeurs annualisées + legacy.
  const minM = aide.montantOriginalMin ?? aide.montantEstimeMin ?? aide.montant_min;
  const maxM = aide.montantOriginalMax ?? aide.montantEstimeMax ?? aide.montant_max;
  const unite = aide.montantUnite ?? aide.unite ?? null;
  const montant = aide.montant_estime_label || formatMontantRange(minM, maxM, unite);

  const documents = aide.documentsRequis || aide.documents_a_fournir || aide.documents_requis || [];
  const motif = aide.raisonTexte || aide.motif;

  return (
    <div style={{
      background:'#FDFCFA', borderRadius:12,
      border:`1.5px solid ${selected ? '#D4724A' : '#E0DDD8'}`,
      marginBottom:10,
    }}>
      <div style={{ padding:'12px 14px', display:'flex', alignItems:'flex-start', gap:10 }}>
        <input type="checkbox" checked={selected} onChange={onToggle}
          style={{ marginTop:3, width:16, height:16, cursor:'pointer', flexShrink:0 }} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:600, fontSize:14, color:'#2D2D2D', marginBottom:4, lineHeight:1.3 }}>
            {aide.nom}
          </div>
          <div style={{ fontSize:12, color:'#8A8A8A', marginBottom:6 }}>
            {aide.organisme}
            {' · '}
            <span style={{ color:'#3D8B5E', fontWeight:500 }}>{montant}</span>
          </div>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
            {hasPct && (
              <Badge color={score >= 70 ? '#3D8B5E' : score >= 50 ? '#D97706' : '#8A8A8A'}>
                {score}% éligible
              </Badge>
            )}
            {aide.priorite === 1 && <Badge color="#D4724A">Prioritaire</Badge>}
            {aide.facilite && <Badge color="#2980B9">{aide.facilite}</Badge>}
            {aide.raisonConfiance && <Badge color="#7B61FF">{aide.raisonConfiance}</Badge>}
            {aide.formulaire_cerfa && <Badge color="#7B61FF">CERFA {aide.formulaire_cerfa}</Badge>}
            {aide.sousConditionRessources && <Badge color="#D97706">⚠️ Sous condition de ressources</Badge>}
          </div>
          {motif && (
            <div style={{ fontSize:12, color:'#5A5A5A', marginTop:6, lineHeight:1.5, fontStyle:'italic' }}>
              {motif}
            </div>
          )}
        </div>
        <button onClick={() => setExpanded(e => !e)} style={{
          padding:'4px 10px', borderRadius:6, border:'1px solid #E0DDD8',
          background:'transparent', fontSize:12, cursor:'pointer', color:'#5A5A5A', flexShrink:0,
        }}>
          {expanded ? '▲' : 'Détails ▼'}
        </button>
      </div>

      {expanded && (
        <div style={{ padding:'0 14px 14px', borderTop:'1px solid #F0EDE8' }}>
          {/* P1 : on lit les nouveaux champs camelCase exposés par
              RecommandationAideDto + fallback snake_case legacy. */}
          <div style={{ fontSize:13, color:'#3A3A3A', lineHeight:1.6, margin:'12px 0' }}>
            {aide.description || motif /* fallback narratif si pas de description riche */}
          </div>
          {aide.note && (
            <div style={{ fontSize:12, color:'#7A6A00', background:'#FFFBEA', border:'1px solid #EDD900', borderRadius:7, padding:'7px 10px', marginBottom:10 }}>
              📌 {aide.note}
            </div>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:'#3D8B5E', textTransform:'uppercase', letterSpacing:0.5, marginBottom:6 }}>
                Conditions d'éligibilité
              </div>
              <div style={{ fontSize:12, color:'#3A3A3A', lineHeight:1.7 }}>
                <div>• Âge min : <strong>{aide.conditions_age ?? 60} ans</strong></div>
                {(aide.conditionsRessourcesTexte || aide.conditions_ressources) && (
                  <div>• Ressources : {aide.conditionsRessourcesTexte || aide.conditions_ressources}</div>
                )}
                {(aide.delaiTraitement || aide.delai_traitement) && (
                  <div>• Délai : {aide.delaiTraitement || aide.delai_traitement}</div>
                )}
                {aide.couverture_geographique && <div>• Couverture : {aide.couverture_geographique}</div>}
                {(aide.urlOfficielle || aide.url_officielle) && (
                  <div>
                    • <a href={aide.urlOfficielle || aide.url_officielle} target="_blank" rel="noopener noreferrer"
                         style={{ color:'#2980B9', textDecoration:'underline' }}>
                      Voir la fiche officielle ↗
                    </a>
                  </div>
                )}
              </div>
            </div>
            <div style={{ background:'#FFF5F5', borderRadius:8, padding:'8px 10px' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#C0392B', textTransform:'uppercase', letterSpacing:0.5, marginBottom:6 }}>
                Causes d'inéligibilité
              </div>
              <div style={{ fontSize:12, color:'#5A3A3A', lineHeight:1.7 }}>
                {inelig.map((p, i) => <div key={i}>• {p}</div>)}
              </div>
            </div>
          </div>

          {documents.length > 0 && (
            <div style={{ marginBottom:10 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#2980B9', textTransform:'uppercase', letterSpacing:0.5, marginBottom:5 }}>
                Documents requis
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                {documents.map((d, i) => (
                  <span key={i} style={{ padding:'3px 9px', borderRadius:20, background:'#EEF4FB', color:'#2C5F8A', fontSize:12 }}>{d}</span>
                ))}
              </div>
            </div>
          )}

          {/* P3 — exemples concrets pour vulgariser au téléphone */}
          {Array.isArray(aide.exemplesConcrets) && aide.exemplesConcrets.length > 0 && (
            <div style={{ marginBottom: 10, background:'#F0F9F4', borderRadius:8, padding:'10px 12px', border:'1px solid #C6E4D2' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#3D8B5E', textTransform:'uppercase', letterSpacing:0.5, marginBottom:5 }}>
                💡 Exemples concrets
              </div>
              <ul style={{ margin:0, paddingLeft:18, fontSize:12, color:'#2A5A40', lineHeight:1.6 }}>
                {aide.exemplesConcrets.map((ex, i) => <li key={i}>{ex}</li>)}
              </ul>
            </div>
          )}

          {/* P3 — pièges courants : à dire AVANT que le client tombe dedans */}
          {Array.isArray(aide.piegesCourants) && aide.piegesCourants.length > 0 && (
            <div style={{ marginBottom: 10, background:'#FFF7EC', borderRadius:8, padding:'10px 12px', border:'1px solid #F0CFA0' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#B27300', textTransform:'uppercase', letterSpacing:0.5, marginBottom:5 }}>
                ⚠️ Pièges à éviter
              </div>
              <ul style={{ margin:0, paddingLeft:18, fontSize:12, color:'#6E4500', lineHeight:1.6 }}>
                {aide.piegesCourants.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}

          {aide.procedure_demande && (
            <div style={{ fontSize:12, color:'#5A5A5A', marginBottom:8 }}>
              <strong>Procédure :</strong> {aide.procedure_demande}
              {aide.organisme_local && <span> — {aide.organisme_local}</span>}
            </div>
          )}

          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:8 }}>
            {aide.url_officielle && (
              <a href={aide.url_officielle} target="_blank" rel="noopener noreferrer" style={{
                padding:'6px 12px', borderRadius:7, background:'#EEF4FB',
                color:'#2980B9', fontSize:12, textDecoration:'none', fontWeight:500,
              }}>Lien officiel ↗</a>
            )}
            {aide.formulaire_url && (
              <a href={aide.formulaire_url} target="_blank" rel="noopener noreferrer" style={{
                padding:'6px 12px', borderRadius:7, background:'#F0FBF4',
                color:'#3D8B5E', fontSize:12, textDecoration:'none', fontWeight:500,
              }}>Formulaire ↗</a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ResultsPhase({ client, clientId, matching, onRestart, chatRef, onError, chat }) {
  const [selected, setSelected]   = useState([]);
  const [tab, setTab]             = useState('aides');
  const [generating, setGenerating] = useState(false);
  const [email, setEmail]         = useState(null);
  const [copied, setCopied]       = useState(false);
  const [saving, setSaving]       = useState(false);

  // Annuaire agents : résout l'agent qui a mené la conversation reprise
  // (visibilité globale → l'agent courant doit savoir si c'est un collègue).
  const { referentLabel } = useAgentDirectory();
  const [aides, setAides]               = useState([]);

  // Le chat (messages, reprise, envoi, session email…) est géré par le hook
  // `useResumableChat` HISSÉ dans le parent → survit aux changements de phase.
  // Ici on ne fait que consommer son API via la prop `chat`.

  useEffect(() => {
    // matching = MatchAidesOutput (Phase 6 / G2). Le legacy exposait
    // `aides_recommandees`, le nouveau expose `recommandations`. On lit
    // les deux pour rester tolérant pendant la transition.
    const list = matching?.recommandations || matching?.aides_recommandees;
    if (Array.isArray(list)) setAides(list);
  }, [matching]);

  const chatRefCallback = (el) => {
    chat.inputRef.current = el;
    if (chatRef) chatRef.current = el;
  };

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  // Phase 6 / G2 — l'envoi d'email est désormais à 2 étapes :
  //  1. Génération du contenu HTML via /api/agent/chat/sessions/:id/messages
  //     en demandant à l'IA de produire un email récapitulatif structuré.
  //  2. Envoi via /api/notification/email { to, subject, bodyHtml }.
  // Pour préserver le UX existant (prévisualisation avant envoi), on
  // sépare "Générer" (étape 1 seule) puis ajoute un bouton "Envoyer"
  // qui appelle l'étape 2.
  const generateEmail = async () => {
    if (!selected.length) return;
    setGenerating(true); setEmail(null);
    try {
      // Session DÉDIÉE à la génération (kind=email_generation), distincte du
      // panneau conversationnel (gérée par le hook) : le prompt + le HTML brut
      // ne polluent pas le chat visible, et cette session est exclue de la reprise.
      const sessionId = await chat.createEmailSession();

      // Construit un prompt pour générer l'email
      const aidesSel = aides.filter(a => selected.includes(a.id || a.aideId));
      // Résumés textuels (strings) attendus par le backend — pas des objets
      const clientCtx = clientContextStr(client);
      const aideContextStr = aidesSel.map(a =>
        `- ${a.nom}${a.organisme ? ` (${a.organisme})` : ''} : ${a.montant_estime_label || a.montantEstime || '—'}`
      ).join('\n');
      const promptIA = "Rédige un email récapitulatif chaleureux et clair pour ce client, listant les aides ci-dessous. Format HTML simple (h2, p, ul/li). Réponds uniquement avec le contenu HTML du corps, sans wrapper <html> ni <body>, sans markdown ni ```html.";

      const msgRes = await agentFetch(`/api/agent/chat/sessions/${sessionId}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          userMessage: promptIA,
          ...(clientCtx && { clientContext: clientCtx }),
          ...(aideContextStr && { aideContext: aideContextStr }),
        }),
      });
      if (!msgRes.ok) throw new Error('chat message failed');
      const out = await msgRes.json();
      const bodyHtml = out.assistantMessage || out.assistantReply || '';
      setEmail({
        email_html: bodyHtml,
        email_text: bodyHtml.replace(/<[^>]+>/g, ''),
      });
    } catch {
      onError?.("La génération de l'email n'a pas abouti. Merci de réessayer dans un instant.");
    }
    finally { setGenerating(false); }
  };

  const sendGeneratedEmail = async () => {
    if (!email?.email_html || !client?.email) return;
    setSaving(true);
    try {
      const res = await agentFetch('/api/notification/email', {
        method: 'POST',
        body: JSON.stringify({
          to: client.email,
          subject: 'Récapitulatif de vos aides — Aidly',
          bodyHtml: email.email_html,
          bodyText: email.email_text,
        }),
      });
      if (!res.ok) throw new Error('send email failed');
    } catch {
      onError?.("L'envoi de l'email a échoué. Merci de réessayer.");
    } finally {
      setSaving(false);
    }
  };

  // L'envoi conversationnel est géré par le hook (`chat.send`).

  // Raccourcis spécifiques à la phase résultats (tab switch)
  // Note Phase 6 / G2 : Ctrl+S (sauvegarde dossier inline) retiré — les
  // modifications de profil se font désormais via PUT /api/agent/leads/:id/profil
  // déclenché par CompleteInterview côté backend (cycle de vie interview).
  useKeyboardShortcuts([
    { key: '1', ctrl: true, handler: () => setTab('aides') },
    { key: '2', ctrl: true, handler: () => setTab('email') },
  ], []);

  const height = 'calc(100vh - 80px)';

  return (
    <div style={{ display:'flex', gap:0, height, overflow:'hidden', borderRadius:14, border:'1px solid #E0DDD8', background:'#FDFCFA' }}>

      {/* ── Dossier à gauche ── */}
      <DossierSidebar client={client} onRestart={onRestart} />

      {/* ── Aides / Email / Notes au centre ── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0, borderRight:'1px solid #E0DDD8', overflow:'hidden' }}>

        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid #E0DDD8', background:'#F5F3F0', flexShrink:0 }}>
          {[['aides',`Aides (${aides.length})`],['email','Email']].map(([key,label]) => (
            <button key={key} onClick={() => setTab(key)} style={{
              padding:'12px 20px', border:'none', cursor:'pointer', fontSize:14,
              fontWeight:tab===key?700:400,
              background:tab===key?'#FDFCFA':'transparent',
              color:tab===key?'#D4724A':'#8A8A8A',
              borderBottom:tab===key?'2px solid #D4724A':'2px solid transparent',
            }}>{label}</button>
          ))}
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:20 }}>

          {/* Tab Aides */}
          {tab === 'aides' && (
            <div>
              {/* Phase 6 / G2 — shape MatchAidesOutput : totalAnnuelMax / messageBilan */}
              {(matching?.totalAnnuelMax ?? matching?.total_annuel_max) > 0 && (
                <div style={{ textAlign:'center', padding:'12px', background:'#FFF0EA', borderRadius:10, marginBottom:16 }}>
                  <div style={{ fontSize:11, color:'#D4724A', fontWeight:700, textTransform:'uppercase', letterSpacing:0.5 }}>Potentiel annuel</div>
                  <div style={{ fontSize:20, fontWeight:700, color:'#D4724A' }}>
                    jusqu'à {(matching.totalAnnuelMax ?? matching.total_annuel_max)?.toLocaleString('fr-FR')} €
                  </div>
                </div>
              )}
              {(matching?.messageBilan || matching?.message_bilan) && (
                <div style={{ fontSize:13, color:'#3A3A3A', background:'#F0FBF4', borderRadius:8, padding:'10px 14px', marginBottom:16, lineHeight:1.6 }}>
                  {matching.messageBilan || matching.message_bilan}
                </div>
              )}
              <div style={{ fontSize:12, color:'#8A8A8A', marginBottom:10 }}>
                Cochez les aides à inclure dans l'email de récapitulatif
              </div>
              {aides.map(aide => {
                // Phase 6 / G2 : RecommandationAideDto utilise `aideId`,
                // le legacy utilisait `id`. On normalise.
                const aideKey = aide.aideId || aide.id;
                return (
                  <AideCard key={aideKey} aide={aide} client={client}
                    selected={selected.includes(aideKey)} onToggle={() => toggle(aideKey)} />
                );
              })}
            </div>
          )}

          {/* Tab Email */}
          {tab === 'email' && (
            <div>
              {selected.length === 0 ? (
                <div style={{ padding:'32px 0', textAlign:'center', color:'#8A8A8A', fontSize:14 }}>
                  Cochez des aides dans l'onglet "Aides" pour générer l'email.
                </div>
              ) : (
                <>
                  <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
                    <button onClick={generateEmail} disabled={generating} style={{
                      padding:'10px 20px', borderRadius:8, border:'none',
                      background:generating?'#D9D9D9':'#D4724A', color:'#fff',
                      fontSize:14, fontWeight:600, cursor:generating?'not-allowed':'pointer',
                    }}>
                      {generating ? 'Génération…' : `Générer l'email (${selected.length} aide${selected.length>1?'s':''})`}
                    </button>
                    {email && (
                      <button onClick={() => { navigator.clipboard.writeText(email.email_text||''); setCopied(true); setTimeout(()=>setCopied(false),2000); }} style={{
                        padding:'10px 16px', borderRadius:8, border:'1px solid #E0DDD8',
                        background:'#fff', fontSize:13, cursor:'pointer', color:copied?'#3D8B5E':'#5A5A5A',
                      }}>
                        {copied ? '✓ Copié' : 'Copier'}
                      </button>
                    )}
                    {email && client?.email && (
                      <button onClick={sendGeneratedEmail} disabled={saving} style={{
                        padding:'10px 16px', borderRadius:8, border:'none',
                        background: saving ? '#D9D9D9' : '#3D8B5E', color:'#fff',
                        fontSize:13, fontWeight:600, cursor: saving ? 'not-allowed' : 'pointer',
                      }}>
                        {saving ? 'Envoi…' : `Envoyer à ${client.email}`}
                      </button>
                    )}
                  </div>
                  {email && (
                    <>
                      <div style={{ fontSize:11, color:'#8A8A8A', fontWeight:700, marginBottom:4 }}>TEXTE BRUT</div>
                      <pre style={{ background:'#F5F3F0', borderRadius:8, padding:14, fontSize:13, lineHeight:1.6, whiteSpace:'pre-wrap', wordBreak:'break-word', color:'#2D2D2D', marginBottom:20 }}>{email.email_text}</pre>
                      <div style={{ fontSize:11, color:'#8A8A8A', fontWeight:700, marginBottom:4 }}>APERÇU HTML</div>
                      <div style={{ background:'#fff', border:'1px solid #E0DDD8', borderRadius:8, padding:16, fontSize:14, lineHeight:1.7 }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(email.email_html || '') }} />
                    </>
                  )}
                </>
              )}
            </div>
          )}

        </div>
      </div>

      {/* ── Chat IA à droite ── */}
      <div style={{ width:320, flexShrink:0, display:'flex', flexDirection:'column', overflow:'hidden' }}>

        <div style={{ padding:'12px 16px', borderBottom:'1px solid #E0DDD8', fontWeight:600, fontSize:14, color:'#2D2D2D', background:'#FDFCFA' }}>
          Questions à l'IA
        </div>

        {/* Bannière de reprise HORS de la zone scrollable (sinon l'auto-scroll
            en bas la pousse hors écran et l'agent ne la voit jamais). */}
        {chat.resumedAt && (
          <div style={{ flexShrink:0, fontSize:12, color:'#8A7250', background:'#FBF3E7', borderBottom:'1px solid #EAD9BF', padding:'6px 10px', display:'flex', alignItems:'center', justifyContent:'center', gap:8, flexWrap:'wrap' }}>
            <span>
              Conversation reprise du {chat.resumedAt.toLocaleDateString('fr-FR')} à {chat.resumedAt.toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' })}
              {chat.resumedAgentId && (
                <> · menée par <ReferentTag label={referentLabel(chat.resumedAgentId)} /></>
              )}
            </span>
            {/* Permet de repartir sur un fil neuf si l'ancien (parfois vieux de
                plusieurs semaines) n'est plus pertinent — évite de polluer le
                contexte IA avec un historique hors-sujet (finding panel #1). */}
            <button
              onClick={chat.startNewConversation}
              style={{ border:'1px solid #EAD9BF', background:'#fff', color:'#8A7250', borderRadius:6, padding:'2px 8px', fontSize:11, cursor:'pointer', whiteSpace:'nowrap' }}
            >
              Nouvelle conversation
            </button>
          </div>
        )}

        <div
          onClick={() => chat.inputRef.current?.focus()}
          style={{ flex:1, overflowY:'auto', padding:14, display:'flex', flexDirection:'column', gap:10, background:'#F5F3F0', cursor:'text' }}
        >
          {chat.messages.length === 0 && (
            <>
              <div style={{ textAlign:'center', color:'#8A8A8A', fontSize:13, marginTop:20, marginBottom:14, lineHeight:1.8 }}>
                Posez une question à Claude<br />sur les aides ou les démarches.
              </div>
              {/* P2 : questions pré-suggérées — l'agent clique sur une chip
                  qui pré-remplit le textarea, gagne 5-10s d'écriture par
                  question. Couvre les 6 questions les + fréquentes selon
                  retour terrain call-center seniors. */}
              <div style={{ display:'flex', flexDirection:'column', gap:6, padding:'0 4px' }}>
                <div style={{ fontSize:11, fontWeight:600, color:'#8A8A8A', textTransform:'uppercase', letterSpacing:0.5, marginBottom:4 }}>
                  Questions fréquentes
                </div>
                {SUGGESTED_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    onClick={() => { chat.setInput(q); chat.inputRef.current?.focus(); }}
                    style={{
                      textAlign:'left', padding:'8px 12px', borderRadius:14,
                      border:'1px solid #E0DDD8', background:'#fff', color:'#3A3A3A',
                      fontSize:12, lineHeight:1.4, cursor:'pointer', fontFamily:'inherit',
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </>
          )}
          {chat.messages.map((m, i) => (
            <React.Fragment key={i}>
              {/* Séparateur entre l'historique repris et les nouveaux messages */}
              {chat.restoredCount > 0 && i === chat.restoredCount && (
                <div style={{ textAlign:'center', fontSize:11, color:'#A89880', margin:'2px 0' }}>
                  — nouveaux messages —
                </div>
              )}
              <div style={{
                maxWidth:'92%', padding:'9px 13px',
                borderRadius:m.role==='user'?'16px 16px 4px 16px':'16px 16px 16px 4px',
                fontSize:13, lineHeight:1.6,
                alignSelf:m.role==='user'?'flex-end':'flex-start',
                background:m.role==='user'?'#D4724A':'#fff',
                color:m.role==='user'?'#fff':'#2D2D2D',
                boxShadow:'0 1px 2px rgba(0,0,0,0.08)',
              }}>
                <div style={{ whiteSpace:'pre-wrap' }}>{m.content}</div>
              </div>
            </React.Fragment>
          ))}
          {chat.sending && (
            <div style={{ alignSelf:'flex-start', padding:'9px 13px', background:'#fff', borderRadius:'16px 16px 16px 4px', fontSize:13, color:'#8A8A8A', boxShadow:'0 1px 2px rgba(0,0,0,0.08)' }}>
              Claude répond…
            </div>
          )}
          <div ref={chat.bottomRef} />
        </div>

        <div style={{ padding:'10px 12px', background:'#FDFCFA', borderTop:'1px solid #E0DDD8', display:'flex', gap:8, alignItems:'flex-end' }}>
          <textarea ref={chatRefCallback} value={chat.input} onChange={e => chat.setInput(e.target.value)}
            onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); chat.send(); } }}
            placeholder="Votre question…"
            rows={2}
            style={{ flex:1, padding:'10px 14px', borderRadius:20, border:'1.5px solid #D9D9D9', fontSize:13, resize:'none', boxSizing:'border-box', outline:'none', fontFamily:'inherit', lineHeight:1.5 }} />
          <button onClick={chat.send} disabled={chat.sending || chat.restoring || !chat.input.trim()} style={{
            width:40, height:40, borderRadius:'50%', border:'none', flexShrink:0,
            background:(chat.sending||chat.restoring||!chat.input.trim())?'#D9D9D9':'#D4724A',
            color:'#fff', fontSize:18, cursor:(chat.sending||chat.restoring||!chat.input.trim())?'not-allowed':'pointer',
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>
            ↑
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal fermeture d'appel ───────────────────────────────────────────────────

const PROPOSITIONS = [
  { key: 'energie',     label: 'Énergie' },
  { key: 'assurance',   label: 'Assurance' },
  { key: 'senior_plus', label: 'Senior-Plus' },
];

function CloseCallModal({ onConfirm, onCancel, saving }) {
  const [notes, setNotes]   = useState('');
  const [duree, setDuree]   = useState('');
  const [props, setProps]   = useState({ energie: 'non_propose', assurance: 'non_propose', senior_plus: 'non_propose' });

  const toggle = (key) => setProps(p => ({ ...p, [key]: p[key] === 'propose' ? 'non_propose' : 'propose' }));

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.4)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000,
    }} onClick={onCancel}>
      <div style={{
        background:'#FDFCFA', borderRadius:16, padding:28, width:420,
        boxShadow:'0 8px 32px rgba(0,0,0,0.18)', border:'1px solid #E0DDD8',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight:700, fontSize:17, color:'#2D2D2D', marginBottom:6 }}>
          Terminer l'appel
        </div>
        <div style={{ fontSize:13, color:'#8A8A8A', marginBottom:20 }}>
          L'appel sera sauvegardé dans l'historique du client.
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block', fontSize:12, color:'#8A8A8A', marginBottom:4 }}>
            Durée (minutes)
          </label>
          <input
            type="number" min="1" value={duree}
            onChange={e => setDuree(e.target.value)}
            placeholder="Ex : 12"
            style={{ width:'100%', padding:'9px 12px', borderRadius:9, border:'1px solid #D9D9D9', fontSize:14, boxSizing:'border-box', outline:'none' }}
          />
        </div>

        {/* Propositions commerciales */}
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:12, color:'#8A8A8A', marginBottom:8, fontWeight:600, textTransform:'uppercase', letterSpacing:0.4 }}>
            Propositions commerciales
          </div>
          <div style={{ background:'#F5F3F0', borderRadius:10, padding:'10px 14px', display:'flex', flexDirection:'column', gap:10 }}>
            {PROPOSITIONS.map(({ key, label }) => {
              const proposed = props[key] === 'propose';
              return (
                <div key={key} style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{ fontSize:13, fontWeight:600, color:'#2D2D2D' }}>{label}</span>
                  <button onClick={() => toggle(key)} style={{
                    padding:'5px 16px', borderRadius:20,
                    border:`1px solid ${proposed ? '#3D8B5E' : '#D9D9D9'}`,
                    background: proposed ? '#F0FBF4' : '#fff',
                    color: proposed ? '#3D8B5E' : '#8A8A8A',
                    fontSize:13, fontWeight: proposed ? 600 : 400, cursor:'pointer',
                    transition:'all 0.15s',
                  }}>
                    {proposed ? '✓ Proposé' : 'Non proposé'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ marginBottom:20 }}>
          <label style={{ display:'block', fontSize:12, color:'#8A8A8A', marginBottom:4 }}>
            Notes de l'appel <span style={{ color:'#C0C0C0' }}>(optionnel)</span>
          </label>
          <textarea
            value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Résumé de l'appel, actions à suivre…"
            rows={3}
            style={{ width:'100%', padding:'9px 12px', borderRadius:9, border:'1px solid #D9D9D9', fontSize:13, resize:'vertical', boxSizing:'border-box', outline:'none' }}
          />
        </div>

        <div style={{ display:'flex', gap:10 }}>
          <button
            onClick={() => onConfirm({ notes, duree_min: duree ? parseInt(duree, 10) : null, propositions: props })}
            disabled={saving}
            style={{
              flex:1, padding:'11px', borderRadius:9, border:'none',
              background: saving ? '#D9D9D9' : '#C0392B', color:'#fff',
              fontSize:14, fontWeight:600, cursor: saving ? 'not-allowed' : 'pointer',
            }}>
            {saving ? 'Sauvegarde…' : 'Terminer et sauvegarder'}
          </button>
          <button onClick={onCancel} style={{
            padding:'11px 18px', borderRadius:9, border:'1px solid #D9D9D9',
            background:'transparent', fontSize:14, color:'#8A8A8A', cursor:'pointer',
          }}>
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Toast inline (erreurs/infos) ──────────────────────────────────────────────

function Toast({ message, tone = 'error', onClose }) {
  useEffect(() => {
    if (!message) return undefined;
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, [message, onClose]);
  if (!message) return null;
  const palette = tone === 'error'
    ? { bg: '#FFF1EF', border: '#E5B4AE', color: '#8B2C1F' }
    : { bg: '#F0FBF4', border: '#BFE5CC', color: '#1F6E3D' };
  return (
    <div role="status" style={{
      position: 'fixed', top: 72, left: '50%', transform: 'translateX(-50%)',
      background: palette.bg, border: `1px solid ${palette.border}`, color: palette.color,
      padding: '10px 18px', borderRadius: 10, fontSize: 14, fontWeight: 500,
      boxShadow: '0 4px 16px rgba(0,0,0,0.08)', zIndex: 2000, maxWidth: 520,
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <span>{message}</span>
      <button onClick={onClose} style={{
        background: 'transparent', border: 'none', color: palette.color,
        fontSize: 16, cursor: 'pointer', padding: 0, lineHeight: 1,
      }} aria-label="Fermer">×</button>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────

export default function AgentSession() {
  const { id }        = useParams();
  const navigate      = useNavigate();
  const [client, setClient]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase]     = useState('interview'); // 'resume' | 'interview' | 'analysing' | 'results'
  const [matching, setMatching] = useState(null);
  const [resume, setResume]   = useState(null);
  const [resumeInterviewId, setResumeInterviewId] = useState(null);
  // Garde anti-rafale BORNÉE (fix 2726× INTERVIEW_DEJA_TERMINEE) : compte les
  // tentatives auto de complétion par interview. On en autorise quelques-unes
  // (un échec aval transitoire — ex. blip réseau sur /matching — doit pouvoir
  // se rejouer), puis on stoppe pour ne pas re-déclencher la boucle
  // render→fail→remount→onComplete à l'infini. Un ref (pas un state) :
  // pas de re-render, persiste sur la vie du composant, se réinitialise à une
  // nouvelle navigation (remontage d'AgentSession). Au-delà du plafond,
  // l'agent recharge la page (F5) pour repartir.
  const completionAttemptsRef = useRef(new Map());
  const MAX_AUTO_COMPLETE_ATTEMPTS = 3;
  const [closeModal, setCloseModal] = useState(false);
  const [closeSaving, setCloseSaving] = useState(false);
  const [toast, setToast] = useState(null); // { message, tone }
  const [helpOpen, setHelpOpen] = useState(false);
  // Sprint Aides Discovery (étape 4) — sous-phase de l'analyse :
  //   'initial'   : appel matching standard (< 3s)
  //   'discovery' : pas de match BDD, fallback web_search en cours (30-60s)
  const [matchingPhase, setMatchingPhase] = useState('initial');
  const globalChatRef = useRef(null);

  const showError = useCallback((message) => setToast({ message, tone: 'error' }), []);
  const showInfo  = useCallback((message) => setToast({ message, tone: 'info'  }), []);

  // Chat assistant HISSÉ ICI (parent) : survit aux changements de phase
  // (résultats ↔ « affiner ») → pas de remontage, donc pas de re-fetch ni de
  // perte d'état ni de bannière « reprise » fausse. Le hook gère reprise,
  // envoi, nouvelle conversation et session de génération d'email.
  const chat = useResumableChat(id, client, {
    buildClientContext: clientContextStr,
    onError: showError,
  });

  useEffect(() => {
    setLoading(true);
    agentFetch(`/api/agent/leads/${id}/resume`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        setResume(data);
        setClient(data.lead);
        if (data.dernierEntretien) setPhase('resume');
        else setPhase('interview'); // vrai nouveau client
      })
      .catch(() => navigate('/agent/clients'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // Phase 6 / G2 — Appelé quand l'IA estime pretPourMatching=true. Le
  // pipeline :
  //  1. POST /api/agent/interviews/:id/complete  → UpdateLeadProfil
  //     (le backend pousse le profil accumulé sur le lead)
  //  2. POST /api/matching/run { clientId, profilSnapshot } → MatchAides
  //  3. GET /api/agent/leads/:id/full → relit le lead à jour
  const handleInterviewComplete = useCallback(async (profilPartiel, interviewId) => {
    // Garde anti-rafale bornée : on plafonne les tentatives auto par
    // interview. Sans ça, en cas d'échec en aval, le cycle catch→
    // setPhase('interview')→remontage d'InterviewPhase→useEffect de
    // chargement→onComplete relance le pipeline en boucle (origine des 2726
    // appels /complete en 4 min, prod 2026-06-25). On laisse quand même
    // quelques essais pour absorber un échec transitoire (réseau, matching).
    if (interviewId) {
      const attempts = completionAttemptsRef.current.get(interviewId) ?? 0;
      if (attempts >= MAX_AUTO_COMPLETE_ATTEMPTS) return;
      completionAttemptsRef.current.set(interviewId, attempts + 1);
    }
    setPhase('analysing');
    setMatchingPhase('initial');

    // Sprint Aides Discovery (étape 4) — si l'appel /api/matching/run dépasse
    // ~3s, c'est très probablement que le matching BDD a échoué et que le
    // backend bascule sur DiscoverAidesFromProfile (web_search Anthropic).
    // On bascule l'overlay sur la version « sources officielles » pour
    // expliquer le wait long (30-60s) à l'agent.
    const discoveryTimer = setTimeout(() => {
      setMatchingPhase('discovery');
    }, 3000);

    try {
      // 1. Complete interview (pousse profil sur lead via UpdateLeadProfil)
      if (interviewId) {
        const completeRes = await agentFetch(`/api/agent/interviews/${interviewId}/complete`, {
          method: 'POST',
          body: JSON.stringify({}),
        });
        if (!completeRes.ok) {
          // Idempotence (fix rafale INTERVIEW_DEJA_TERMINEE — 2726 occurrences
          // en prod le 2026-06-25). `CompleteInterview` pousse le profil PUIS
          // termine l'interview : recevoir DÉJÀ_TERMINÉE implique donc qu'un
          // complete précédent a réussi (profil bien persisté sur le lead) et
          // qu'un ré-essai du pipeline a relancé /complete. On poursuit
          // (recharge + matching) au lieu de remonter une erreur fatale.
          // Toute AUTRE erreur reste fatale (profil non persisté → reprise vide).
          // Le body d'erreur backend (error-handler) a la forme
          // { error: <CODE>, message }, donc le code est dans `error`.
          const errBody = await completeRes.json().catch(() => ({}));
          if (errBody?.error !== 'INTERVIEW_DEJA_TERMINEE') {
            throw new Error(`complete failed: ${completeRes.status}`);
          }
        }
      }

      // 2. Recharge le lead : son `profil` est désormais normalisé (camelCase,
      //    via UpdateLeadProfil) avec le revenu estimé + la situation familiale.
      //    On matche SUR CE PROFIL, pas sur la sortie brute (snake_case) de l'IA —
      //    sinon revenu / logement / situation seraient perdus (toMatchingSnapshot
      //    attend un LeadProfilDto camelCase).
      const updated = await agentFetch(`/api/agent/leads/${id}/full`).then(r => r.json());
      const profilMatching = updated?.profil || profilPartiel;

      // 3. Lance le matching sur le profil normalisé du lead
      const snap = toMatchingSnapshot(profilMatching);
      if (!snap || !snap.age || !snap.departement) {
        throw new Error('profil incomplet pour matching');
      }
      const matchRes = await agentFetch('/api/matching/run', {
        method:'POST',
        body:JSON.stringify({
          clientId: id,
          profilSnapshot: snap,
        }),
      });
      if (!matchRes.ok) throw new Error('matching failed');
      const matchData = await matchRes.json();

      setClient(updated);
      setMatching(matchData);
      setPhase('results');

      // Détection du marqueur [Discovery] ajouté par MatchAides (backend)
      // quand un fallback web_search a été déclenché. Les aides retournées
      // ont alors le statut `a_verifier` côté BDD et nécessitent validation
      // admin avant d'être réutilisées.
      const bilan = matchData?.messageBilan || matchData?.message_bilan || '';
      if (bilan.includes('[Discovery]')) {
        showInfo("Nouvelles aides découvertes depuis les sources officielles (à valider par un admin). Elles apparaissent dans la liste.");
      }
    } catch {
      setPhase('interview');
      showError("Impossible d'analyser les aides pour l'instant. Merci de réessayer dans un instant.");
    } finally {
      clearTimeout(discoveryTimer);
      setMatchingPhase('initial');
    }
  }, [id, showError, showInfo]);

  // Clôture d'appel — enregistre le bilan (notes + durée + propositions)
  // dans l'HISTORIQUE du client via une interaction (type 'note'), ce que
  // la modale promet explicitement (« sauvegardé dans l'historique »).
  //
  // ⚠️ Ne PAS repasser par UpdateLeadStatus : (1) il ignore totalement le
  // champ `raison` → la note était perdue à tous les coups ; (2) forcer
  // `nouveau → qualifié` viole la machine à états (400 TRANSITION_LEAD_
  // INVALIDE, jusqu'ici avalé en silence). Le statut du lead se gère à la
  // main via la liste des leads, qui respecte déjà les transitions.
  const handleCloseCall = useCallback(async ({ notes, duree_min, propositions }) => {
    setCloseSaving(true);
    try {
      const note = buildCloseCallNote({ notes, duree_min, propositions });
      // Rien d'exploitable saisi → rien à sauvegarder (le domaine refuse
      // une note vide) : on navigue simplement.
      if (note) {
        const res = await agentFetch(`/api/agent/leads/${id}/interactions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'note', note }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || `Erreur ${res.status}`);
        }
      }
      navigate('/agent/clients');
    } catch {
      // On garde la modale OUVERTE : les notes saisies restent à l'écran,
      // l'agent peut réessayer sans rien reperdre.
      setCloseSaving(false);
      showError("La sauvegarde de l'appel n'a pas pu aboutir. Vos notes sont toujours là — réessayez.");
    }
  }, [id, navigate, showError]);

  const restart = useCallback(async () => {
    // Phase 6 / G2 — pas besoin de "reset" sur le backend : une nouvelle
    // interview démarre en POST /api/agent/interviews et le matching
    // sera relancé via /api/matching/run. On reset juste l'état local.
    setResumeInterviewId(null);
    setMatching(null);
    setPhase('interview');
  }, []);

  // ── Raccourcis clavier globaux (Esc, Ctrl+Enter, ?) ───────────────────────
  useKeyboardShortcuts([
    {
      key: 'Escape',
      allowInInput: true,
      handler: () => {
        // Priorité : fermer modales ouvertes avant de naviguer
        if (helpOpen) { setHelpOpen(false); return; }
        if (closeModal) { setCloseModal(false); return; }
        if (toast) { setToast(null); return; }
        navigate('/agent/clients');
      },
    },
    {
      key: 'Enter', ctrl: true,
      allowInInput: true,
      handler: () => {
        if (helpOpen || closeModal) return;
        setCloseModal(true);
      },
    },
    {
      key: '?',
      allowInInput: true,
      handler: () => setHelpOpen(o => !o),
    },
  ], [helpOpen, closeModal, toast, navigate]);

  if (loading) return (
    <AgentLayout>
      <div style={{ textAlign:'center', padding:60, color:'#8A8A8A' }}>Chargement du dossier…</div>
    </AgentLayout>
  );
  if (!client) return null;

  const headerButtons = (
    <>
      <button onClick={() => navigate('/agent/clients')} style={{
        padding: '6px 14px', borderRadius: 8, border: '1px solid #D1CCC2',
        background: 'transparent', color: '#5A3C1F',
        fontSize: 13, fontWeight: 500, cursor: 'pointer',
      }}>
        ← Retour aux clients
      </button>
      <button onClick={() => navigate(`/agent/clients/${id}/history`)} style={{
        padding: '6px 14px', borderRadius: 8, border: '1px solid #D1CCC2',
        background: '#FFF5F0', color: '#D4724A',
        fontSize: 13, fontWeight: 500, cursor: 'pointer',
      }}>
        📜 Historique
      </button>
      <button onClick={() => setCloseModal(true)} style={{
        padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(192,57,43,0.4)',
        background: 'rgba(192,57,43,0.07)', color: '#C0392B',
        fontSize: 13, fontWeight: 600, cursor: 'pointer',
      }}>
        ☎ Terminer l'appel
      </button>
    </>
  );

  return (
    <AgentLayout
      title={phase === 'results' ? fullName(client) : `Entretien — ${fullName(client)}`}
      headerActions={headerButtons}
    >
      <Toast message={toast?.message} tone={toast?.tone} onClose={() => setToast(null)} />
      {phase === 'resume' && (
        <ResumePhase
          client={client}
          dernierEntretien={resume?.dernierEntretien}
          dernierMatching={resume?.dernierMatching}
          onContinue={() => { setResumeInterviewId(resume.dernierEntretien.id); setPhase('interview'); }}
          onVoirAides={() => { setMatching(resume.dernierMatching); setPhase('results'); }}
          onRecommencer={() => { setResumeInterviewId(null); setResume(r => ({ ...r, dernierEntretien: null })); setPhase('interview'); }}
        />
      )}
      {phase === 'interview' && (
        <InterviewPhase client={client} clientId={id} resumeInterviewId={resumeInterviewId} onComplete={handleInterviewComplete} onError={showError} />
      )}
      {phase === 'analysing' && <AnalysingPhase matchingPhase={matchingPhase} />}
      {phase === 'results' && (
        <ResultsPhase client={client} clientId={id} matching={matching} onRestart={restart}
          chatRef={globalChatRef} onError={showError} chat={chat} />
      )}
      {closeModal && (
        <CloseCallModal
          onConfirm={handleCloseCall}
          onCancel={() => setCloseModal(false)}
          saving={closeSaving}
        />
      )}
      <ShortcutsHelpModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        shortcuts={SESSION_SHORTCUTS}
      />
      <ShortcutsHint onClick={() => setHelpOpen(true)} />
    </AgentLayout>
  );
}
